<!-- Bloomberg Terminal Pro Navbar -->
<header
  class="bb-navbar sticky top-0 z-sticky flex items-center gap-2 border-b border-bb-border bg-bb-bg-secondary h-12 px-3"
  [class.bb-navbar--live]="isLiveMode()"
  [attr.data-trading-mode]="isLiveMode() ? 'live' : 'paper'">
  <!-- Mobile menu button -->
  <button
    type="button"
    class="lg:hidden p-1.5 rounded text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors"
    (click)="toggleMobileMenu()"
    aria-controls="mobile-navigation-dialog"
    aria-haspopup="dialog"
    [attr.aria-expanded]="mobileMenuOpen()"
    aria-label="Open menu">
    <ui-icon name="bars" size="md" aria-hidden="true" />
  </button>

  <!-- Logo (mobile only - desktop has sidebar) -->
  <div class="flex items-center gap-2 lg:hidden">
    <div class="w-7 h-7 rounded-lg bg-bb-accent-blue flex items-center justify-center">
      <span class="font-mono font-bold text-xs text-bb-bg-primary">R</span>
    </div>
    <span class="font-display font-semibold text-bb-text-primary tracking-tight text-sm">RAZOR</span>
  </div>

  <!-- #22 · Mobile connection fallback. The md+ center-status block and the
       app-global-websocket-status pill are both hidden below 768px, leaving no
       connection cue on mobile. This compact dot reuses the existing
       isConnected() computed and complements the md:+ block (never both at
       once). role=status + aria-label keep the dot-only cue accessible
       (WCAG — never colour/shape alone).
       RF-QA-195: rendered only below `md` via the breakpoint signal (not
       CSS-hidden) so no hidden live region lingers in the a11y tree. -->
  @if (!isMdUp()) {
    <div
      class="flex md:hidden items-center gap-1.5"
      role="status"
      [attr.aria-label]="isConnected() ? 'Connection: connected' : 'Connection: disconnected'">
      @if (isConnected()) {
        <span class="w-1.5 h-1.5 rounded-full bg-bb-accent-green animate-pulse" aria-hidden="true"></span>
      } @else {
        <span class="w-1.5 h-1.5 rounded-full bg-bb-accent-red" aria-hidden="true"></span>
      }
    </div>
  }

  <!-- Paper Trading Badge.
       Desktop (md+): full badge with the "· {env}" label.
       Mobile (<md): compact variant (glyph + PAPER/LIVE/UNKNOWN, no env label).
       Both render the SAME component / tri-state source of truth.
       RF-QA-195: exactly ONE instance is in the DOM per breakpoint (@if on the
       breakpoint signal). The previous `hidden md:block` / `flex md:hidden`
       twins were both in the a11y tree, so the role=status live region
       announced "PAPER TRADING updated …" twice. The mobile variant restores
       the PAPER-vs-LIVE cue that was lost when the dashboard's local mode pill
       was removed (the dashboard relies on this navbar badge). -->
  @if (isMdUp()) {
    <!-- RF-QA-165: nowrap — at 768px "PAPER · SANDBOX" broke over two lines. -->
    <app-paper-trading-badge class="block whitespace-nowrap"></app-paper-trading-badge>
  } @else {
    <app-paper-trading-badge compact class="flex"></app-paper-trading-badge>
  }

  <!-- Spacer -->
  <div class="flex-1"></div>

  <!-- Center Status Indicators -->
  <!-- RF-QA-165: nowrap, and the clock only from lg — at 768px (no sidebar yet,
       but hamburger + logo) the pills and the clock wrapped to two lines each. -->
  <div class="hidden md:flex items-center gap-4 text-xs whitespace-nowrap">
    <!-- Connection Status -->
    <div class="flex items-center gap-1.5">
      @if (isConnected()) {
        <span class="w-1.5 h-1.5 rounded-full bg-bb-accent-green animate-pulse"></span>
        <span data-testid="navbar-connection-text" class="font-mono text-bb-accent-green uppercase tracking-wider">Connected</span>
      } @else {
        <span class="w-1.5 h-1.5 rounded-full bg-bb-accent-red"></span>
        <span data-testid="navbar-connection-text" class="font-mono text-bb-accent-red uppercase tracking-wider">Disconnected</span>
      }
    </div>

    <!-- Separator -->
    <div class="w-px h-4 bg-bb-border"></div>

    <!-- System Status -->
    <div class="flex items-center gap-1.5">
      @switch (systemStatus()) {
        @case ('online') {
          <span class="px-1.5 py-0.5 rounded bg-bb-accent-green/15 text-bb-accent-green font-mono text-2xs uppercase tracking-wider">
            WS Online
          </span>
        }
        @case ('degraded') {
          <span class="px-1.5 py-0.5 rounded bg-bb-accent-amber/15 text-bb-accent-amber font-mono text-2xs uppercase tracking-wider">
            WS Degraded
          </span>
        }
        @case ('offline') {
          <span class="px-1.5 py-0.5 rounded bg-bb-accent-red/15 text-bb-accent-red font-mono text-2xs uppercase tracking-wider">
            WS Offline
          </span>
        }
      }
    </div>

    <!-- Separator -->
    <div class="hidden lg:block w-px h-4 bg-bb-border"></div>

    <!-- Live Clock -->
    <div class="hidden lg:block font-mono text-bb-text-secondary tabular-nums">
      {{ dateTime.formatTime(currentTime(), true) }} {{ dateTime.timezone() }}
    </div>
  </div>

  <!-- GAP-FE-039: Global WebSocket status indicator -->
  <app-global-websocket-status class="hidden md:block"></app-global-websocket-status>

  <!-- Right side actions -->
  <div class="flex items-center gap-1">
    <!-- GAP-FE-038: Language selector (gated by multiLanguageSupport feature flag) -->
    @if (multiLanguageEnabled) {
      <app-language-selector class="hidden lg:block"></app-language-selector>
    }

    <!-- Search on desktop and mobile. Opens the SAME surface as ⌘K (the
         command palette) so the button's hotkey hint is truthful. -->
    <button
      id="global-search"
      type="button"
      (click)="openSearch()"
      class="flex items-center gap-2 px-2.5 py-1.5 rounded-md border border-bb-border bg-bb-bg-tertiary/50 text-bb-text-muted hover:text-bb-text-secondary hover:border-bb-border-strong transition-colors group cursor-pointer"
      [attr.aria-label]="'Open search (' + shortcutModifier + 'K)'">
      <ui-icon name="search" size="sm" aria-hidden="true" />
      <span class="hidden lg:inline text-xs">Search</span>
      <kbd class="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-bb-bg-primary border border-bb-border text-2xs font-mono text-bb-text-muted">
        <span class="text-3xs">{{ shortcutModifier }}</span>K
      </kbd>
    </button>

    <!-- Notifications -->
    <app-notification-center></app-notification-center>

    <!-- Profile menu -->
    <div class="relative" #profileMenu>
      <button
        type="button"
        class="flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-bb-bg-tertiary transition-colors"
        (click)="toggleProfileMenu()"
        aria-label="Profile menu"
        aria-haspopup="true"
        [attr.aria-expanded]="showProfileMenu()"
        aria-controls="profile-dropdown">
        <div class="w-6 h-6 rounded bg-bb-accent-blue/15 flex items-center justify-center">
          <span data-testid="navbar-avatar" class="text-2xs font-mono font-medium text-bb-accent-blue">{{ userInitial() }}</span>
        </div>
        <ui-icon name="chevron-down" size="xs" class="text-bb-text-muted transition-transform"
                 [class.rotate-180]="showProfileMenu()" aria-hidden="true" />
      </button>

      <!-- Profile dropdown -->
      @if (showProfileMenu()) {
        <div id="profile-dropdown" role="menu" class="absolute right-0 mt-1 w-48 rounded-lg border border-bb-border bg-bb-bg-secondary shadow-xl shadow-black/30 animate-fade-in-down overflow-hidden">
          <div class="px-3 py-2 border-b border-bb-border">
            <p class="text-xs font-medium text-bb-text-primary truncate">{{ userDisplayName() }}</p>
            <p class="text-2xs text-bb-text-muted truncate">{{ userEmail() }}</p>
            <p data-testid="navbar-user-role" class="text-2xs text-bb-text-muted mt-0.5 font-mono uppercase">{{ userRole() }}</p>
          </div>
          <div class="p-1">
            <a routerLink="/settings/profile" role="menuitem"
               class="flex items-center gap-2 px-2 py-1.5 rounded text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors">
              <ui-icon name="user" size="sm" aria-hidden="true" />
              <span class="text-xs">Profile</span>
            </a>
            <a routerLink="/settings" role="menuitem"
               class="flex items-center gap-2 px-2 py-1.5 rounded text-bb-text-secondary hover:text-bb-text-primary hover:bg-bb-bg-tertiary transition-colors">
              <ui-icon name="cog" size="sm" aria-hidden="true" />
              <span class="text-xs">Settings</span>
            </a>
          </div>
          <div class="border-t border-bb-border p-1">
            <button
              type="button"
              role="menuitem"
              (click)="logout()"
              class="flex items-center gap-2 w-full px-2 py-1.5 rounded text-bb-accent-red hover:bg-bb-accent-red/10 transition-colors">
              <ui-icon name="sign-out" size="sm" aria-hidden="true" />
              <span class="text-xs font-medium">Logout</span>
            </button>
          </div>
        </div>
      }
    </div>
  </div>
</header>
